import { Link, useParams, Navigate } from "react-router-dom";
import { useMemo } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta, articleJsonLd, breadcrumbJsonLd } from "../lib/usePageMeta";
import { CAREER_RESOURCES, findResource } from "../lib/career-resources";
import { ArrowLeft, Clock, Sparkles, Quote } from "lucide-react";

export default function CareerResourceArticle() {
  const { slug } = useParams();
  const article = useMemo(() => findResource(slug), [slug]);

  usePageMeta(
    article
      ? {
          seo: {
            title: `${article.title} · CV Folks`,
            description: article.excerpt,
            keywords: article.tags.join(", "),
          },
          jsonLd: [
            articleJsonLd({
              title: article.title,
              description: article.excerpt,
              slug: article.slug,
              updated: article.updated,
            }),
            breadcrumbJsonLd([
              { name: "Home", url: "https://cvfolks.co.uk/" },
              { name: "Career Resources", url: "https://cvfolks.co.uk/career-resources" },
              { name: article.title, url: `https://cvfolks.co.uk/career-resources/${article.slug}` },
            ]),
          ],
        }
      : null,
  );

  if (!article) return <Navigate to="/career-resources" replace />;

  const related = CAREER_RESOURCES.filter((r) => r.slug !== article.slug).slice(0, 3);

  return (
    <>
      <Navbar />
      <main
        data-testid={`career-article-${article.slug}`}
        className="pt-24 md:pt-32 bg-background pb-24"
      >
        <article className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/career-resources"
            className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-brand"
          >
            <ArrowLeft className="h-4 w-4" /> All resources
          </Link>

          <div className="mt-8">
            <div className="flex gap-2 text-[11px] font-semibold uppercase tracking-widest text-brand mb-3">
              {article.tags.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold leading-[1.08]">
              {article.title}
            </h1>
            <div className="mt-4 flex items-center gap-4 text-xs text-foreground/60">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {article.read_min} min read
              </span>
              <span>Updated {article.updated}</span>
            </div>
          </div>

          {/* Hero stat */}
          {article.hero_stat && (
            <div className="mt-10 rounded-3xl border border-brand/25 bg-brand/5 p-8 flex items-center gap-6">
              <p className="font-serif text-5xl md:text-6xl font-bold text-brand shrink-0">
                {article.hero_stat}
              </p>
              <p className="text-sm md:text-base text-foreground/80">
                {article.hero_stat_label}
              </p>
            </div>
          )}

          {/* Body */}
          <div className="mt-10 space-y-6 text-[16px] md:text-[17px] leading-[1.7] text-foreground/85">
            {article.blocks.map((b, i) => {
              if (b.type === "h2") {
                return (
                  <h2 key={i} className="font-serif text-2xl md:text-3xl font-bold text-foreground pt-4">
                    {b.text}
                  </h2>
                );
              }
              if (b.type === "p") {
                return <p key={i}>{b.text}</p>;
              }
              if (b.type === "ul") {
                return (
                  <ul key={i} className="list-disc pl-6 space-y-2">
                    {b.items.map((it, j) => (
                      <li key={j}>{it}</li>
                    ))}
                  </ul>
                );
              }
              if (b.type === "quote") {
                return (
                  <blockquote
                    key={i}
                    className="border-l-4 border-brand pl-5 my-8 italic text-lg text-foreground/85"
                  >
                    <Quote className="h-5 w-5 text-brand mb-2" />
                    {b.text}
                    {b.cite && (
                      <cite className="block mt-2 not-italic text-xs text-foreground/60">
                        — {b.cite}
                      </cite>
                    )}
                  </blockquote>
                );
              }
              return null;
            })}
          </div>

          {/* CTA */}
          <div className="mt-14 rounded-3xl bg-foreground text-background p-8 text-center">
            <Sparkles className="h-8 w-8 mx-auto text-brand" />
            <h2 className="font-serif text-2xl md:text-3xl font-bold mt-3">
              Skip the guesswork.
            </h2>
            <p className="text-base text-background/70 mt-3 max-w-xl mx-auto">
              Try our AI CV Builder for £9.99 or hire a UK writer from £50.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link
                to="/ai-cv-maker-builder"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:opacity-90"
              >
                Try AI CV Builder — £9.99
              </Link>
              <Link
                to="/services/cv-writing"
                className="inline-flex items-center gap-2 rounded-full border border-background/40 px-6 py-3 text-sm font-semibold hover:bg-background/10"
              >
                Hire a UK writer
              </Link>
            </div>
          </div>
        </article>

        {/* Related */}
        <section className="max-w-5xl mx-auto px-6 md:px-10 mt-20">
          <h2 className="font-serif text-2xl md:text-3xl font-bold">Keep reading.</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/career-resources/${r.slug}`}
                data-testid={`related-${r.slug}`}
                className="rounded-2xl border border-border bg-card p-5 hover:border-brand transition"
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-brand">
                  {r.tags[0]}
                </p>
                <h3 className="font-serif text-lg font-bold mt-2 line-clamp-2">
                  {r.title}
                </h3>
                <p className="text-xs text-foreground/60 mt-2">
                  {r.read_min} min read →
                </p>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
